import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Download, Eye, FileText, X } from 'lucide-react';
import { toast } from 'sonner';

export interface Document {
    label: string;
    url?: string;
    fileName?: string;
}

interface DocumentViewerProps {
    documents: Document[];
    onDownload?: (url: string, fileName: string) => void;
    gridCols?: 2 | 3 | 4;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({
    documents,
    onDownload,
    gridCols = 3,
}) => {
    const [selectedDoc, setSelectedDoc] = useState<Document | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleView = (doc: Document) => {
        if (!doc.url) {
            toast.error('Document not available');
            return;
        }
        console.log('Opening document preview:', doc);
        setSelectedDoc(doc);
        setIsModalOpen(true);
    };

    const handleDownload = (doc: Document) => {
        if (!doc.url) {
            toast.error('Document not available');
            return;
        }

        if (onDownload) {
            onDownload(doc.url, doc.fileName || doc.label);
        } else {
            // Default download behavior
            const link = document.createElement('a');
            link.href = doc.url;
            link.download = doc.fileName || doc.label;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            toast.success('Download started');
        }
    };

    const isPDF = (url?: string) => {
        return url?.toLowerCase().endsWith('.pdf') || url?.includes('application/pdf');
    };

    const isImage = (url?: string) => {
        return url?.match(/\.(jpg|jpeg|png|gif|webp)$/i) || url?.startsWith('data:image');
    };

    const gridColsClass = {
        2: 'grid-cols-1 md:grid-cols-2',
        3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
        4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
    };

    return (
        <>
            <div className={`grid ${gridColsClass[gridCols]} gap-4`}>
                {documents.map((doc, index) => (
                    <Card key={index} className="overflow-hidden">
                        <CardContent className="p-4">
                            {/* Preview Area */}
                            <div className="aspect-video bg-muted rounded-lg mb-3 overflow-hidden relative group">
                                {doc.url ? (
                                    <>
                                        {isImage(doc.url) ? (
                                            <img
                                                src={doc.url}
                                                alt={doc.label}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : isPDF(doc.url) ? (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <FileText className="w-16 h-16 text-muted-foreground" />
                                            </div>
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <FileText className="w-16 h-16 text-muted-foreground" />
                                            </div>
                                        )}

                                        {/* Hover Overlay */}
                                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                            <Button
                                                size="sm"
                                                variant="secondary"
                                                onClick={() => handleView(doc)}
                                            >
                                                <Eye className="w-4 h-4 mr-1" />
                                                View
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="secondary"
                                                onClick={() => handleDownload(doc)}
                                            >
                                                <Download className="w-4 h-4 mr-1" />
                                                Download
                                            </Button>
                                        </div>
                                    </>
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                        <div className="text-center">
                                            <X className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                                            <p className="text-sm text-muted-foreground">Not uploaded</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Document Info */}
                            <div>
                                <p className="font-medium text-sm mb-2">{doc.label}</p>
                                <div className="flex gap-2">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="flex-1"
                                        onClick={() => handleView(doc)}
                                        disabled={!doc.url}
                                    >
                                        <Eye className="w-3 h-3 mr-1" />
                                        View
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="flex-1"
                                        onClick={() => handleDownload(doc)}
                                        disabled={!doc.url}
                                    >
                                        <Download className="w-3 h-3 mr-1" />
                                        Download
                                    </Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Fullscreen Modal */}
            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                <DialogContent className="max-w-4xl max-h-[90vh]">
                    <DialogHeader>
                        <DialogTitle className="flex items-center justify-between">
                            <span>{selectedDoc?.label}</span>
                            {selectedDoc && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleDownload(selectedDoc)}
                                >
                                    <Download className="w-4 h-4 mr-2" />
                                    Download
                                </Button>
                            )}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="overflow-auto max-h-[calc(90vh-8rem)]">
                        {selectedDoc?.url ? (
                            <>
                                {isImage(selectedDoc.url) ? (
                                    <img
                                        src={selectedDoc.url}
                                        alt={selectedDoc.label}
                                        className="w-full h-auto"
                                        onError={(e) => {
                                            console.error('Image failed to load:', selectedDoc.url);
                                            toast.error('Failed to load image');
                                        }}
                                    />
                                ) : isPDF(selectedDoc.url) ? (
                                    <div className="space-y-4">
                                        <iframe
                                            src={selectedDoc.url}
                                            className="w-full h-[70vh] border rounded"
                                            title={selectedDoc.label}
                                            onError={(e) => {
                                                console.error('PDF iframe failed to load:', selectedDoc.url);
                                            }}
                                        />
                                        <div className="text-center text-sm text-muted-foreground">
                                            <p>If the preview doesn't load, try downloading the document.</p>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => window.open(selectedDoc.url, '_blank')}
                                                className="mt-2"
                                            >
                                                Open in New Tab
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center py-12">
                                        <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                                        <p className="text-muted-foreground mb-4">
                                            Preview not available for this file type
                                        </p>
                                        <div className="flex gap-2 justify-center">
                                            <Button onClick={() => handleDownload(selectedDoc)}>
                                                <Download className="w-4 h-4 mr-2" />
                                                Download File
                                            </Button>
                                            <Button
                                                variant="outline"
                                                onClick={() => window.open(selectedDoc.url, '_blank')}
                                            >
                                                Open in New Tab
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="text-center py-12">
                                <X className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                                <p className="text-muted-foreground">Document not available</p>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default DocumentViewer;
